import React, { useCallback, useEffect } from "react";
import { FormikCheck, FormikControl, FormSection } from "@gelsenwasser/react";
import { Row, Col, Container } from "react-bootstrap";
import { ErrorMessage, useFormikContext } from "formik";

import L, { latLng, LatLng, LatLngExpression } from "leaflet";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";

import "leaflet/dist/leaflet.css";

// im Standard wird die URL für die Icons irgendwie komisch erstellt (mit Base64 Data-URL *und* dem Icon-Namen), was zu Fehlern in der Darstellung führt
delete L.Icon.Default.prototype["_getIconUrl"];
import i1 from "leaflet/dist/images/marker-icon-2x.png";
import i2 from "leaflet/dist/images/marker-icon.png";
import i3 from "leaflet/dist/images/marker-shadow.png";
L.Icon.Default.mergeOptions({ iconRetinaUrl: i1, iconUrl: i2, shadowUrl: i3 });

import { Anfrage } from "../Types";
import { useLocationSearch } from "./hooks/useLocationSearch";
import { useStandortStore } from "stores/useStandortStore";
import ModalAddressDialog from "components/ModalAddressDialog";
import debounce from "lodash.debounce";

interface MyMapProps {
  center?: LatLngExpression;
  onPositionClicked?: (latLng: LatLng) => void;
}
const MyMap = ({ center, onPositionClicked }: MyMapProps) => {
  useMapEvents({
    click(e) {
      if (onPositionClicked) onPositionClicked(e.latlng);
    },
  });

  const map = useMap();
  if (center) {
    map.panTo(center);
  }

  return null;
};

const ProjektstandortStep: React.VFC = () => {
  // stores
  const isValidStandort = useStandortStore((state) => state.isValidStandort);

  // hooks
  const { touched, errors, values, setFieldValue } = useFormikContext<Anfrage>();
  const {
    defaultMapCenter,
    markerRef,
    kommune,
    position,
    handlePositionClicked,
    eventHandlers,
    isPositionFetched,
    isPositionLoading,
  } = useLocationSearch<Anfrage>({
    projektstandort: values.projektstandort,
    projektstandortValueName: "projektstandort",
    setFieldValue,
  });

  // states
  const [showAddressDialog, setShowAddressDialog] = React.useState(false);

  const debouncedDialog = useCallback(
    debounce(
      (
        karteAktiv: boolean,
        isValidStandort: boolean | undefined,
        isPositionFetched: boolean,
        isPositionLoading: boolean
      ) => {
        if (!isPositionLoading && isPositionFetched && !karteAktiv)
          setShowAddressDialog(isValidStandort !== undefined && !isValidStandort);
      },
      5000
    ),
    []
  );

  // parameter
  // queries und mutationen
  // effekte
  useEffect(() => {
    debouncedDialog(values.projektstandort.karteAktiv, isValidStandort, isPositionFetched, isPositionLoading);
  }, [isValidStandort]);

  // daten
  // handler
  const handleCloseAddressDialog = () => setShowAddressDialog(false);

  return (
    <FormSection title="">
      <p>
        Bitte geben SIe die genaue Adresse ein, für die die Löschwasserbescheinigung benötigt wird. Alternativ können
        Sie in der Karte den Marker an der genauen Adresse positionieren. Falls vorhanden können Sie uns zur genauen
        Zuordnung noch Flur und Flurstücksnr. zusätzlich mitteilen.
        {/* Fügen Sie Ihren Standort als Adressdaten ein. Wenn keine Adresse vorhanden ist, fügen Sie ihre Flurnummer ein.
        Sie können auch alternativ die Karte benutzen und den Pin auf Ihren Standort verschieben/setzen. */}
      </p>
      <Row>
        <Col md={6}>
          <Container>
            <Row>
              <Col md={8}>
                <FormikControl
                  name="projektstandort.strasse"
                  label="Strasse"
                  disabled={values.projektstandort.karteAktiv}
                />
              </Col>
              <Col>
                <FormikControl
                  name="projektstandort.hausnummer"
                  label="Hausnummer"
                />
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <FormikControl name="projektstandort.plz" label="PLZ" disabled={values.projektstandort.karteAktiv} />
              </Col>
              <Col>
                <FormikControl name="projektstandort.name" label="Ort" disabled />
              </Col>
            </Row>
            <Row>
              <Col>
                <FormikCheck
                  type="switch"
                  label="Karte benutzen"
                  value="false"
                  checked={values.projektstandort.karteAktiv ?? false}
                  name="projektstandort.karteAktiv"
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <strong>Zusatzinformation</strong>
              </Col>
            </Row>
            <Row>
              <Col md={4}>
                <FormikControl name="projektstandort.flur" label="Flur" />
              </Col>
              <Col>
                <FormikControl name="projektstandort.flurstueck" label="Flurstück" />
              </Col>
            </Row>
            {touched.projektstandort?.flur &&
              touched.projektstandort?.flurstueck &&
              errors.projektstandort &&
              typeof errors.projektstandort === "string" && (
                <Row>
                  <Col>
                    <small style={{ color: "red" }}>
                      <ErrorMessage name="projektstandort" />
                    </small>
                  </Col>
                </Row>
              )}
          </Container>
        </Col>
        <Col>
          <MapContainer
            center={kommune ? latLng(kommune.lat, kommune.lng) : defaultMapCenter}
            zoom={13}
            style={{ height: "100%", minHeight: "300px" }}
          >
            <MyMap center={position} onPositionClicked={handlePositionClicked} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {position && (
              <Marker
                position={position}
                ref={markerRef}
                eventHandlers={eventHandlers}
                draggable={values.projektstandort.karteAktiv}
              />
            )}
          </MapContainer>
        </Col>
      </Row>
      {isValidStandort !== undefined && !isValidStandort && (
        <>
          <Row>
            <Col className="text-danger font-weight-bolder">
              Bitte prüfen Sie Ihre Adresseingabe, ob auch das richtige Objekt in der Karte angezeigt wird!!!{" "}
              {!values.projektstandort?.hausnummer && <>Bitte geben Sie eine Hausnummer an!</>}
            </Col>
          </Row>
          <ModalAddressDialog showDialog={showAddressDialog} onClose={handleCloseAddressDialog} />
        </>
      )}
    </FormSection>
  );
};

export default ProjektstandortStep;
